import { useCallback } from 'react';
import { SurveyQuestion } from '../types/cancellation';
import { QUESTION_TYPES, SPECIAL_ANSWERS } from '../constants/cancellation';

interface UseCancellationQuestionProps {
    question: SurveyQuestion;
    answer: string | string[] | undefined;
    onAnswer: (answer: string | string[]) => void;
}

export const useCancellationQuestion = ({ question, answer, onAnswer }: UseCancellationQuestionProps) => {
    // Handle single select option
    const handleSelectOption = useCallback(
        (dbMap: string) => {
            onAnswer(dbMap);
        },
        [onAnswer]
    );

    // Handle multi-select toggle
    const handleMultiSelectToggle = useCallback(
        (dbMap: string) => {
            const currentAnswers = Array.isArray(answer) ? answer : [];
            const newAnswers = currentAnswers.includes(dbMap)
                ? currentAnswers.filter(a => a !== dbMap)
                : [...currentAnswers, dbMap];
            onAnswer(newAnswers);
        },
        [answer, onAnswer]
    );

    // Handle text change for main text questions
    const handleTextChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onAnswer(e.target.value);
        },
        [onAnswer]
    );

    // Handle "Other" text input
    const handleOtherTextChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const otherPrefix = `${SPECIAL_ANSWERS.OTHER}: `;
            if (question.type === QUESTION_TYPES.SELECT) {
                // For select, store the text directly as "other: <text>"
                onAnswer(`${otherPrefix}${e.target.value}`);
            } else if (question.type === QUESTION_TYPES.MULTI_SELECT) {
                // For multiSelect, update the "other" entry
                const currentAnswers = Array.isArray(answer) ? answer : [];
                const withoutOther = currentAnswers.filter(a => !String(a).startsWith(SPECIAL_ANSWERS.OTHER));
                if (e.target.value.trim()) {
                    onAnswer([...withoutOther, `${otherPrefix}${e.target.value}`]);
                } else {
                    onAnswer(withoutOther);
                }
            }
        },
        [question.type, answer, onAnswer]
    );

    // Get "Other" text value
    const getOtherText = useCallback(() => {
        const otherPrefix = `${SPECIAL_ANSWERS.OTHER}: `;
        if (question.type === QUESTION_TYPES.SELECT && typeof answer === 'string' && answer.startsWith(otherPrefix)) {
            return answer.substring(otherPrefix.length);
        }
        if (question.type === QUESTION_TYPES.MULTI_SELECT && Array.isArray(answer)) {
            const otherAnswer = answer.find(a => typeof a === 'string' && a.startsWith(otherPrefix));
            return otherAnswer ? String(otherAnswer).substring(otherPrefix.length) : '';
        }
        return '';
    }, [question.type, answer]);

    // Check if "Other" is selected for single select
    const isOtherSelectedForSelect =
        question.type === QUESTION_TYPES.SELECT &&
        (typeof answer === 'string' && answer.startsWith(`${SPECIAL_ANSWERS.OTHER}: `)
            ? SPECIAL_ANSWERS.OTHER
            : answer) === SPECIAL_ANSWERS.OTHER;

    // Get selected db maps for multi-select (handles "Other" prefix)
    const getSelectedDbMaps = useCallback(() => {
        if (question.type !== QUESTION_TYPES.MULTI_SELECT) return [];
        const selectedAnswers = Array.isArray(answer) ? answer : [];
        const otherPrefix = `${SPECIAL_ANSWERS.OTHER}: `;
        return selectedAnswers.map(a =>
            typeof a === 'string' && a.startsWith(otherPrefix) ? SPECIAL_ANSWERS.OTHER : a
        );
    }, [question.type, answer]);

    // Check if "Other" is selected for multi-select
    const isOtherSelectedForMultiSelect =
        question.type === QUESTION_TYPES.MULTI_SELECT && getSelectedDbMaps().includes(SPECIAL_ANSWERS.OTHER);

    return {
        handleSelectOption,
        handleMultiSelectToggle,
        handleTextChange,
        handleOtherTextChange,
        getOtherText,
        isOtherSelectedForSelect,
        isOtherSelectedForMultiSelect,
        getSelectedDbMaps,
    };
};
